import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, debounceTime, Observable, switchMap, tap } from 'rxjs';
import { PagedResult } from 'src/app/shared/models/paging';
import { FilteringService } from 'src/app/shared/services/filtering.service';
import { AllocatedPriceSign, PriceSignSearchEvent, UnallocatedPriceSign } from '../price-sign.model';
import { PriceSignService } from 'src/app/shared/services/price-sign.service';
import { PagerBase } from 'src/app/shared/models/pagerBase';

interface FilterState {
  query: string | null;
  sort: Sort | null;
  page: PageEvent;
}

export enum IncidentsQueryParams {
  query = 'query',
}

@Injectable()
export class PriceSignListStateService extends PagerBase {
  activatedRoute = inject(ActivatedRoute);
  router = inject(Router);
  filteringService = inject(FilteringService);
  priceSignService = inject(PriceSignService);
  readonly queryParamMap$ = toSignal(this.activatedRoute.queryParamMap);
  public loading: WritableSignal<boolean> = signal<boolean>(false);
  private refreshTrigger = signal(0);
  public pageSizeOptions = signal<number[]>(this.pageSizes);

  private queryParamValue(key: IncidentsQueryParams): string | null {
    return this.queryParamMap$()?.get(key) ?? null;
  }

  public filters: WritableSignal<FilterState> = signal<FilterState>({
    query: this.queryParamValue(IncidentsQueryParams.query)
      ? decodeURIComponent(this.queryParamValue(IncidentsQueryParams.query) as string)
      : null,
    sort: null,
    page: {
      length: 0,
      pageIndex: 0,
      pageSize: this.defaultPageSize,
    },
  });
  $unallocatedPriceSigns: Observable<UnallocatedPriceSign[]> = combineLatest([toObservable(this.refreshTrigger)]).pipe(
    switchMap(() => this.priceSignService.getUnallocatedPriceSigns())
  );
  $priceSigns: Observable<PagedResult<AllocatedPriceSign>> = combineLatest([
    toObservable(this.filters),
    toObservable(this.refreshTrigger),
  ]).pipe(
    debounceTime(300),
    tap(() => this.loading.set(true)),
    switchMap(([filters, refresh]) => {
      const searchEvent = <PriceSignSearchEvent>{
        query: filters.query ? filters.query : null,
        paging: filters.page,
        sorting: filters.sort,
      };
      return this.priceSignService.getAllocatedPriceSigns(searchEvent);
    }),
    tap(result => {
      this.loading.set(false);
    })
  );

  triggerUpdate() {
    this.refreshTrigger.update(n => n + 1);
  }

  public resetFilter() {
    this.filters.update(current => ({
      ...current,
      query: null,
      sort: null,
      page: { length: 0, pageIndex: 0, pageSize: this.defaultPageSize },
    }));
  }
}
